import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2 } from 'lucide-react';
import { employeeApi } from '../services/api';
import type { EmployeeHierarchyResponse } from '../types';
import { OrgChartBranch, OrgChartPersonCard } from './OrgChartNodes';

type Props = {
  employeeId: number | null;
  onClose: () => void;
};

const EmployeeHierarchyModal: React.FC<Props> = ({ employeeId, onClose }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<EmployeeHierarchyResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (employeeId == null) {
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    employeeApi
      .getHierarchy(employeeId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const msg =
            err && typeof err === 'object' && 'response' in err
              ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
              : null;
          setError(msg || 'Could not load hierarchy.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  useEffect(() => {
    if (employeeId == null) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [employeeId, onClose]);

  const openProfile = (id: number) => {
    onClose();
    navigate(`/employees/${id}`);
  };

  if (employeeId == null) return null;

  const titleName = data
    ? `${data.focus.firstName} ${data.focus.lastName}`
    : 'Employee';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hierarchy-modal-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-neutral-200">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-neutral-200 bg-neutral-50">
          <h2 id="hierarchy-modal-title" className="text-lg font-semibold text-neutral-900">
            Reporting hierarchy — {titleName}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md text-neutral-500 hover:text-neutral-800 hover:bg-neutral-200/80"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-4 py-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-neutral-600">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <span className="text-sm">Loading hierarchy…</span>
            </div>
          )}

          {error && !loading && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">{error}</div>
          )}

          {data && !loading && !error && (
            <section>
              <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wide mb-4 text-center">
                Management chain
              </h3>
              <div className="flex flex-col items-center">
                {data.ancestors.length === 0 ? (
                  <p className="text-sm text-neutral-500 text-center mb-2">No line manager set (top of chain).</p>
                ) : (
                  <>
                    {data.ancestors.map((a, idx) => (
                      <React.Fragment key={a.id}>
                        <OrgChartPersonCard person={a} variant="default" onOpenProfile={openProfile} />
                        {idx < data.ancestors.length - 1 && (
                          <div className="h-4 w-px bg-neutral-300 my-1" aria-hidden />
                        )}
                      </React.Fragment>
                    ))}
                    <div className="h-4 w-px bg-neutral-300 my-1" aria-hidden />
                  </>
                )}
                <OrgChartPersonCard person={data.focus} variant="focus" onOpenProfile={openProfile} />
                {data.reports.length > 0 && (
                  <div className="flex flex-col items-center w-full">
                    {data.reports.length === 1 ? (
                      <>
                        <div className="h-4 w-px bg-neutral-300 my-1 shrink-0" aria-hidden />
                        <OrgChartBranch node={data.reports[0]} onOpenProfile={openProfile} />
                      </>
                    ) : (
                      <>
                        <div className="h-4 w-px bg-neutral-300 my-1 shrink-0" aria-hidden />
                        <div className="w-full max-w-4xl border-t border-neutral-300 flex flex-row flex-wrap justify-center gap-x-10 gap-y-6 items-start px-2">
                          {data.reports.map((r) => (
                            <div key={r.id} className="flex flex-col items-center min-w-[10rem] max-w-[16rem]">
                              <div className="h-3 w-px bg-neutral-300 shrink-0" aria-hidden />
                              <OrgChartBranch node={r} onOpenProfile={openProfile} />
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeHierarchyModal;
